/**
 * lib/visual/pulse.ts — Prompt 1 / Part 4
 *
 * Sidebar slice-pulse system. Mutations across the app emit a pulse for the
 * slice they touched; the Sidebar's NavRow listens and flashes amber for
 * 1500ms. This is what makes the demo cascade visible — clicking Reject on
 * one screen lights up Bookings → Audit → Outbox in sequence.
 *
 * Mechanism: a CustomEvent dispatched on `window`. No store, no React-tree
 * dependency, no global state singleton. The hook listens; the emitter
 * fires-and-forgets. Safe under SSR (the `window` checks are required —
 * Next.js will otherwise blow up during the server render pass).
 */

import { useEffect, useState } from 'react';

export type SliceKey =
  | 'roster'
  | 'projection'
  | 'bookings'
  | 'hotelApproval'
  | 'crew'
  | 'dashboard'
  | 'reports'
  | 'invoices'
  | 'analytics'
  | 'audit'
  | 'outbox'
  | 'events'
  | 'communication'
  | 'broadcasts'
  | 'inventoryAlerts'
  | 'feedback'
  // Prompt 2 / Part 2/3/4 — Hotel master, RFP wizard, Nearest-crew finder.
  | 'hotels'
  | 'rfp'
  | 'crewAssignments';

const EVENT_NAME = 'rezolv:slice-pulse';
const PULSE_DURATION_MS = 1500;

export interface SlicePulseDetail {
  slice: SliceKey;
}

export function emitPulse(slice: SliceKey): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<SlicePulseDetail>(EVENT_NAME, { detail: { slice } }),
  );
}

/**
 * React hook — returns true for `PULSE_DURATION_MS` after the named slice
 * fires. Cancels prior timers if a second pulse arrives mid-flash so the
 * effect re-arms cleanly under rapid bursts.
 */
export function useSlicePulse(slice: SliceKey): boolean {
  const [pulsing, setPulsing] = useState(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const handler = (e: Event): void => {
      const ce = e as CustomEvent<SlicePulseDetail>;
      if (ce.detail?.slice !== slice) return;
      setPulsing(true);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setPulsing(false), PULSE_DURATION_MS);
    };
    window.addEventListener(EVENT_NAME, handler);
    return () => {
      window.removeEventListener(EVENT_NAME, handler);
      if (timer) clearTimeout(timer);
    };
  }, [slice]);

  return pulsing;
}
